// PointableItem — Phase 2.5d.
//
// Wraps a template item (a step, a card, a chip, a node…) so the agent can
// emit `signal_stage_event({event:'point_at', target:'X'})` and have the orb
// morph to that exact item. The wrapper:
//
//   1. Stamps `data-orb-target` on its outer div so external tools (and the
//      Playwright smoke) can locate it.
//   2. Reads usePointingState() and, when its id is the current target,
//      renders a <DefaultOrbAnchor>-equivalent inline overlay containing
//      the canonical <Orb layoutId="orb" /> at this position.
//
// Templates also render a `<DefaultOrbAnchor>` somewhere stable (top-right,
// etc). DefaultOrbAnchor renders the orb only when no item is currently
// pointed-to. Because both the default anchor and the per-item anchor share
// `layoutId="orb"`, exactly one orb exists at any time — Framer Motion
// animates its position between them automatically.

import type { ReactNode } from 'react';
import { Orb } from './Orb';
import { usePointingState } from './usePointingState';

interface PointableItemProps {
  /** The id the agent will pass as `target`. Must be unique within a scene. */
  orbTargetId: string;
  /** Diameter of the orb when it lands here. Item-sized: usually 40–56px. */
  orbSize?: number;
  /** Inline-style overlay placement. Default: top-right corner of the item. */
  orbPlacement?: 'top-right' | 'top-left' | 'top-center' | 'center';
  className?: string;
  children: ReactNode;
}

export function PointableItem({
  orbTargetId,
  orbSize = 48,
  orbPlacement = 'top-right',
  className = '',
  children,
}: PointableItemProps) {
  const { currentTarget } = usePointingState();
  const active = currentTarget === orbTargetId;
  // Tailwind absolute placement utilities; kept tiny so we don't reach into
  // template-specific layouts. Templates that want a custom landing point
  // can drop their own pointable shells using <DefaultOrbAnchor> directly.
  const placementClass =
    orbPlacement === 'top-left'   ? 'top-2 left-2' :
    orbPlacement === 'top-center' ? 'top-2 left-1/2 -translate-x-1/2' :
    orbPlacement === 'center'     ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' :
    /* top-right */                 'top-2 right-2';
  return (
    <div data-orb-target={orbTargetId} className={`relative ${className}`}>
      {children}
      {active && (
        <div className={`absolute ${placementClass} pointer-events-none z-10`}>
          <Orb size={orbSize} />
        </div>
      )}
    </div>
  );
}

/**
 * Renders the orb at the template's natural anchor position UNLESS the agent
 * is currently pointing at a specific item. Use this in place of <Orb /> in
 * each template's anchor slot.
 */
export function DefaultOrbAnchor({ size = 48, className = '' }: { size?: number; className?: string }) {
  const { currentTarget } = usePointingState();
  if (currentTarget) return null;
  return <Orb size={size} className={className} />;
}
